import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { MagnifyingGlass, Faders, X } from '@phosphor-icons/react'
import { api } from '../lib/api'
import type { SearchResult, SearchFilters } from '../hooks/useSearch'

const CREW_OPTIONS = [
  { value: '', label: 'All crews' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'stage', label: 'Stage' },
  { value: 'it', label: 'IT' },
  { value: 'all', label: 'All crews (general)' },
]

const TEAM_OPTIONS = [
  { value: '', label: 'All teams' },
  { value: 'installation', label: 'Installation' },
  { value: 'operations', label: 'Operations' },
]

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const crewFilter = searchParams.get('crew') || ''
  const teamFilter = searchParams.get('team') || ''
  const phaseFilter = searchParams.get('phase') || ''

  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [inputValue, setInputValue] = useState(query)

  useEffect(() => {
    setInputValue(query)
  }, [query])

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    async function performSearch() {
      setLoading(true)
      setError(null)
      try {
        const filters: SearchFilters = {}
        if (crewFilter) filters.crew = crewFilter
        if (teamFilter) filters.team = teamFilter
        if (phaseFilter) filters.phase = phaseFilter

        const data = await api.search({ q: query, ...filters })
        setResults(data.results || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed')
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    performSearch()
  }, [query, crewFilter, teamFilter, phaseFilter])

  const updateSearch = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        next.set(key, value)
      } else {
        next.delete(key)
      }
    })
    setSearchParams(next)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      updateSearch({ q: inputValue.trim() })
    }
  }

  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text
    const words = query
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 1)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    if (words.length === 0) return text

    const regex = new RegExp(`(${words.join('|')})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="search-highlight">{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      )
    )
  }

  return (
    <div className="search-results-page">
      <div className="search-results-page__header">
        <h1>Search</h1>

        <form onSubmit={handleSubmit} className="search-results-page__form">
          <div className="search-results-page__input-wrapper">
            <MagnifyingGlass
              size={20}
              weight="bold"
              className="search-results-page__icon"
              aria-hidden="true"
            />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search modules..."
              aria-label="Search modules"
              className="search-results-page__input"
            />
            {inputValue && (
              <button
                type="button"
                className="search-results-page__clear"
                onClick={() => {
                  setInputValue('')
                  updateSearch({ q: '' })
                }}
                aria-label="Clear search"
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
          <button type="submit" className="btn btn-primary">
            Search
          </button>
          <button
            type="button"
            className={`btn btn-secondary ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
          >
            <Faders size={16} weight="bold" />
            Filters
          </button>
        </form>

        {showFilters && (
          <div className="search-results-page__filters">
            <div className="filter-group">
              <label htmlFor="filter-crew">Crew</label>
              <select
                id="filter-crew"
                value={crewFilter}
                onChange={(e) => updateSearch({ crew: e.target.value })}
              >
                {CREW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-team">Team</label>
              <select
                id="filter-team"
                value={teamFilter}
                onChange={(e) => updateSearch({ team: e.target.value })}
              >
                {TEAM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="filter-phase">Phase</label>
              <input
                id="filter-phase"
                type="text"
                value={phaseFilter}
                onChange={(e) => updateSearch({ phase: e.target.value })}
                placeholder="e.g. foundation, core..."
              />
            </div>

            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                updateSearch({ crew: '', team: '', phase: '' })
              }
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="search-results-page__loading">Searching...</div>
      )}

      {error && (
        <div className="search-results-page__error">{error}</div>
      )}

      {!loading && !error && query && (
        <div className="search-results-page__stats">
          {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
          {(crewFilter || teamFilter || phaseFilter) && ' (filtered)'}
        </div>
      )}

      {!loading && !error && results.length > 0 && (
        <div className="search-results-page__list">
          {results.map((result) => (
            <Link
              key={result.id}
              to={result.path}
              className="search-result-card"
            >
              <div className="search-result-card__header">
                <h3>{highlightMatch(result.title, query)}</h3>
                <div className="search-result-card__meta">
                  <span className={`crew-badge crew-badge--${result.crew}`}>
                    {result.crew}
                  </span>
                  <span>{result.teams.join(' / ')}</span>
                  <span className="stage-badge">{result.stage}</span>
                </div>
              </div>
              {result.snippet && (
                <p className="search-result-card__snippet">
                  {highlightMatch(result.snippet, query)}
                </p>
              )}
              <div className="search-result-card__footer">
                <span className="search-result-card__phase">
                  Phase: {result.phase}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && !error && query && results.length === 0 && (
        <div className="search-results-page__empty">
          <MagnifyingGlass size={48} weight="light" />
          <h2>No results found</h2>
          <p>
            No modules matched "{query}".
            {(crewFilter || teamFilter || phaseFilter) && (
              <> Try adjusting your filters. </>
            )}
          </p>
        </div>
      )}

      {!query && (
        <div className="search-results-page__empty">
          <MagnifyingGlass size={48} weight="light" />
          <h2>Search modules</h2>
          <p>Enter a search term above to find training modules.</p>
        </div>
      )}
    </div>
  )
}
